import {
  CONFIRMATION_FLOWS,
  ConfirmationFlow,
  COUNTER_TYPES,
  CounterType,
  IDENTIFICATION_METHODS,
  MarketingData,
  PreviousInhabitant,
  SIGN_CHANNELS,
  SignChannel,
  SIMULATION_TYPES,
  SimulationType,
  Situation,
  SITUATIONS
} from '@boltenergy-be/shared'
import { ElectricityAndGasPrices } from 'features/simulation/types'
import { MarketingDataKeys, MarketingStore } from 'store/marketing/types'
import { MeterDetailsInputs } from 'store/customer/types'
import dayjs from 'dayjs'

/**
 * Returns the fixed prices for API requests
 *
 * @param {ElectricityAndGasPrices} fixedPrices
 * @param {SimulationType} simulationType
 * @returns {ElectricityAndGasPrices|undefined}
 */
export const getFixedPricesForApiRequests = (
  fixedPrices: ElectricityAndGasPrices,
  simulationType: SimulationType
): ElectricityAndGasPrices | undefined => {
  if (simulationType === SIMULATION_TYPES.fixedAmount) {
    if (fixedPrices) return fixedPrices

    return {
      electricity: 0,
      gas: 0
    }
  }

  return undefined
}

/**
 * Returns the confirmation flow for API requests based on the marketing data
 * @param {MarketingStore} marketing
 * @returns {ConfirmationFlow}
 */
const getConfirmationFlow = (marketing: MarketingStore): ConfirmationFlow => {
  // CONFIRMATION_FLOWS.awaitingPlentyInvestment is used in the investmentstatussync, not here.
  if (marketing[MarketingDataKeys.EMAIL_CONFIRMATION_FLOW]) return CONFIRMATION_FLOWS.email
  if (marketing[MarketingDataKeys.CREDIT_CHECK_CONFIRMATION_FLOW]) return CONFIRMATION_FLOWS.creditCheck
  return undefined
}

/**
 * Returns the shared marketing data for CreateCustomer & UpsertProspect request bodies
 * @param {MarketingStore} marketing
 * @param {SimulationType} simulationType
 * @param {SignChannel} signChannel
 * @returns {Omit<MarketingData, "signChannel" | "optinCta" | "optinPoint" | "eanCaptureType" | "noEanFlow" | "creditCheckFlow">}
 */
export const getSharedMarketingDataForRequestBody = (
  marketing: MarketingStore,
  simulationType: SimulationType,
  signChannel: SignChannel
): Omit<MarketingData, 'signChannel' | 'optinCta' | 'optinPoint' | 'eanCaptureType' | 'noEanFlow' | 'creditCheckFlow'> => {
  return {
    agentID: marketing[MarketingDataKeys.AGENT_ID],
    identificationMethod:
      signChannel === SIGN_CHANNELS.signature ? IDENTIFICATION_METHODS.default : marketing[MarketingDataKeys.IDENTIFICATION_METHOD],
    callCampaign: marketing[MarketingDataKeys.CALL_CAMPAIGN],
    callPartnerAccount: marketing[MarketingDataKeys.CALL_PARTNER_ACCOUNT],
    campaignName: marketing[MarketingDataKeys.CAMPAIGN_NAME],
    confirmationFlow: getConfirmationFlow(marketing),
    eventID: marketing[MarketingDataKeys.EVENT_ID],
    eventName: marketing[MarketingDataKeys.EVENT_NAME],
    gclid: marketing[MarketingDataKeys.GCLID] ?? undefined,
    partnerID: marketing[MarketingDataKeys.PARTNER_ID],
    sfUserAlias: marketing[MarketingDataKeys.SALESFORCE_USER_ALIAS],
    simulationSalesOffice:
      marketing[MarketingDataKeys.SIMULATION_SO] && Array.isArray(marketing.simulationSo)
        ? marketing[MarketingDataKeys.SIMULATION_SO][0]
        : marketing[MarketingDataKeys.SIMULATION_SO],
    simulationType,
    sourceType: marketing[MarketingDataKeys.SOURCE_TYPE]
  }
}

/**
 * Returns the previous inhabitant data for CreateContract and CreateCustomer request body
 * @param {MeterDetailsInputs["previousInhabitant"]} previousInhabitant
 * @returns {{email: string, firstName: string, lastName: string, supplier: string, clientType: "Residential" | "Professional", address: {streetName: string, streetNumber: string, streetBox: string, postalCode: number, townName: string}} | undefined}
 */
export const getPreviousInhabitant = (previousInhabitant?: MeterDetailsInputs['previousInhabitant']): PreviousInhabitant | undefined => {
  if (!previousInhabitant) return undefined

  const passAddress =
    previousInhabitant?.address?.streetName && previousInhabitant?.address?.streetNumber && previousInhabitant?.address?.postalCode

  // Only pass object if at least one relevant field is filled
  return passAddress || previousInhabitant.email || previousInhabitant.firstName || previousInhabitant.lastName
    ? ({
        email: previousInhabitant.email || undefined,
        firstName: previousInhabitant.firstName || undefined,
        lastName: previousInhabitant.lastName || undefined,
        supplier: previousInhabitant.supplier || undefined,
        clientType: previousInhabitant.clientType,
        address: passAddress
          ? {
              streetName: previousInhabitant.address.streetName || undefined,
              streetNumber: previousInhabitant.address.streetNumber || undefined,
              streetBox: previousInhabitant.address.streetBox || undefined,
              postalCode: previousInhabitant.address.postalCode ? Number(previousInhabitant.address.postalCode) : undefined,
              townName: previousInhabitant.address.townName || undefined
            }
          : undefined
      } satisfies PreviousInhabitant)
    : undefined
}

/**
 * Checks if the user can pass indexes & meter numbers or only EAN
 * @param {Situation} situation
 * @param {CounterType} counterType
 * @param {string} contractStartDate
 * @returns {boolean}
 */
export const checkCanPassIndexesAndMeterNumbers = (situation: Situation, counterType: CounterType, contractStartDate: string): boolean => {
  return situation === SITUATIONS.move && counterType === COUNTER_TYPES.analog && !dayjs(contractStartDate).isAfter(dayjs())
}
